import React, { useState, useCallback } from 'react';
import { Upload, ArrowLeft, X, CheckCircle, AlertCircle, FileEdit } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    Box,
    Container,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Button,
    FormControl,
    FormHelperText,
    LinearProgress,
    IconButton,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import fileDownload from 'js-file-download';
import {
    useDownloadPositionTemplateMutation,
    useBulkUploadPositionsMutation,
    useDownloadBulkEditTemplateMutation,
    useBulkEditPositionsMutation,
} from '../../../api/preHQ';
const StyledContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3),
}));

const StyledCard = styled(Card)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    margin: '0 auto',
}));

const DropZone = styled(Box)(({ theme, isDragOver }) => ({
    border: `2px dashed ${isDragOver ? '#ED7342' : '#FFE4D6'}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
    backgroundColor: isDragOver ? '#FFF4F0' : '#FFF8F6',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: '#FFF4F0',
        borderColor: '#ED7342',
    },
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default function BulkUpload() {
    const navigate = useNavigate();
    const location = useLocation();

    // Flag to determine if this is bulk edit or bulk upload
    const isBulkEdit = location.pathname.includes('bulk-edit') || location.search.includes('mode=edit');

    const [file, setFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [downloadPositionTemplate, { isLoading: isDownloadingTemplate }] = useDownloadPositionTemplateMutation();
    const [downloadBulkEditTemplate, { isLoading: isDownloadingBulkEditTemplate }] =
        useDownloadBulkEditTemplateMutation();
    const [bulkEditPositions, { isLoading: isUploadingBulkEditPositions }] = useBulkEditPositionsMutation();
    const [bulkUploadPositions, { isLoading: isUploadingPositions }] = useBulkUploadPositionsMutation();
    const validateFile = (selectedFile) => {
        const validTypes = [
            'text/csv',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
        ];
        const validExtensions = ['.csv', '.xlsx', '.xls'];

        const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));

        return validTypes.includes(selectedFile.type) || validExtensions.includes(fileExtension);
    };

    const handleFileChange = (selectedFile) => {
        if (selectedFile && validateFile(selectedFile)) {
            setFile(selectedFile);
            toast.success('File selected successfully');
        } else {
            toast.error('Invalid file type. Please upload a CSV or Excel file');
        }
    };

    const handleFileInputChange = (e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            handleFileChange(selectedFile);
        }
    };

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);

        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            handleFileChange(droppedFile);
        }
    }, []);

    const handleTemplateDownload = async () => {
        try {
            const response = await (isBulkEdit
                ? downloadBulkEditTemplate().unwrap()
                : downloadPositionTemplate().unwrap());

            if (!response) return;
            if (response.status === 'ERROR') throw new Error(response?.message);

            const fileName = isBulkEdit ? 'BulkEdit-PositionData.xlsx' : 'BulkUpload-PositionData.xlsx';
            fileDownload(new Blob([response]), fileName);

            toast.success('Template downloaded successfully');
        } catch (error) {
            console.error('Template download error:', error);
            toast.error('Failed to download template');
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Please select a file to upload');
            return;
        }

        // File validation enhancements
        const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB in bytes
        const ALLOWED_TYPES = [
            'text/csv',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
        ];
        const ALLOWED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

        // Check file size
        if (file.size > MAX_FILE_SIZE) {
            toast.error(`File size exceeds 25MB limit. Current size: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
            return;
        }

        // Enhanced file type validation
        const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        const isValidType = ALLOWED_TYPES.includes(file.type) || ALLOWED_EXTENSIONS.includes(fileExtension);

        if (!isValidType) {
            toast.error('Invalid file type. Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
            return;
        }

        // Security checks
        if (file.name.includes('..') || file.name.includes('/') || file.name.includes('\\')) {
            toast.error('Invalid file name detected. Please use a valid file name.');
            return;
        }
        const dangerousExtensions = ['.exe', '.bat', '.cmd', '.com', '.pif', '.scr', '.vbs', '.js', '.jar'];
        if (dangerousExtensions.some((ext) => file.name.toLowerCase().includes(ext))) {
            toast.error('File type not allowed');
            return;
        }

        // Clear previous results
        setUploadResult(null);

        try {
            const formData = new FormData();
            formData.append('dataFile', file);

            // Add flag to indicate if this is bulk edit
            if (isBulkEdit) {
                formData.append('isBulkEdit', 'true');
            }

            const response = await (isBulkEdit
                ? bulkEditPositions(formData).unwrap()
                : bulkUploadPositions(formData).unwrap());

            // Set upload result dynamically based on response status
            const resultType =
                response.status === 'ERROR'
                    ? 'error'
                    : response.status === 'PARTIAL_SUCCESS'
                      ? 'partial_success'
                      : 'success';

            setUploadResult({
                type: resultType,
                status: response.status,
                message: response.message,
                errors: response.errors || [],
                positions: response.positions || [],
                totalRows: response.totalRows,
                successCount: response.successCount,
                errorCount: response.errorCount,
            });
        } catch (error) {
            const response = error.data || {};

            setUploadResult({
                type: 'error',
                status: response.status || 'ERROR',
                message: response.message || 'There was an error uploading your file',
                errors: response.errors || [error?.errors || 'Something went wrong'],
                positions: response.positions || [],
                totalRows: response.totalRows || 0,
                successCount: response.successCount || 0,
                errorCount: response.errorCount || 1,
            });
        }
    };

    const handleRemoveFile = () => {
        setFile(null);
        setUploadResult(null);
    };

    const clearResults = () => {
        setUploadResult(null);
    };

    return (
        <StyledContainer maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    variant="text"
                    startIcon={<ArrowLeft size={16} />}
                    onClick={() => navigate('/client/positions')}
                    sx={{
                        color: '#215263',
                        fontSize: '0.75rem',
                        '&:hover': {
                            color: '#ED7342',
                            backgroundColor: '#FFF4F0',
                        },
                    }}
                >
                    Back to Home
                </Button>
            </Box>

            <StyledCard>
                <CardHeader
                    title={
                        <Typography variant="h5" component="h1" sx={{ color: '#215263' }}>
                            {isBulkEdit ? 'Bulk Edit Positions' : 'Bulk Upload Positions'}
                        </Typography>
                    }
                    subheader={
                        <Box>
                            <Typography variant="body2" sx={{ color: '#7C85A2', mt: 2, mb: 1 }}>
                                {isBulkEdit
                                    ? 'Upload a CSV or Excel file containing position updates'
                                    : 'Upload a CSV or Excel file containing multiple positions'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#7C85A2' }}>
                                To download a template file{' '}
                                <Box
                                    component="span"
                                    onClick={handleTemplateDownload}
                                    sx={{
                                        color: '#ED7342',
                                        cursor:
                                            isDownloadingTemplate || isDownloadingBulkEditTemplate
                                                ? 'not-allowed'
                                                : 'pointer',
                                        fontWeight: 'bold',
                                        opacity: isDownloadingTemplate || isDownloadingBulkEditTemplate ? 0.6 : 1,
                                        '&:hover': {
                                            color:
                                                isDownloadingTemplate || isDownloadingBulkEditTemplate
                                                    ? '#ED7342'
                                                    : '#D65A2A',
                                        },
                                    }}
                                >
                                    {isDownloadingTemplate || isDownloadingBulkEditTemplate
                                        ? 'Downloading...'
                                        : 'Click here'}
                                </Box>
                            </Typography>
                        </Box>
                    }
                />
                <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl>
                            <DropZone
                                isDragOver={isDragOver}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={() => document.getElementById('file-input').click()}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: '#4A5568',
                                        mb: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    Drag and drop a file here or click here to upload
                                </Typography>
                                {isBulkEdit ? (
                                    <FileEdit size={48} color="#ED7342" style={{ marginBottom: '16px' }} />
                                ) : (
                                    <Upload size={48} color="#ED7342" style={{ marginBottom: '16px' }} />
                                )}
                                <VisuallyHiddenInput
                                    id="file-input"
                                    type="file"
                                    accept=".csv,.xlsx,.xls"
                                    onChange={handleFileInputChange}
                                />
                            </DropZone>
                            {file && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        backgroundColor: '#F5F5F5',
                                        borderRadius: 2,
                                        padding: 2,
                                        mt: 2,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#4A5568',
                                            fontWeight: 500,
                                            flex: 1,
                                        }}
                                    >
                                        {file.name}
                                    </Typography>
                                    <IconButton
                                        onClick={handleRemoveFile}
                                        size="small"
                                        sx={{
                                            color: '#9CA3AF',
                                            '&:hover': {
                                                color: '#EF4444',
                                                backgroundColor: '#FEE2E2',
                                            },
                                        }}
                                    >
                                        <X size={16} />
                                    </IconButton>
                                </Box>
                            )}
                            <FormHelperText sx={{ color: '#7C85A2' }}>Accepted formats: CSV, XLSX, XLS</FormHelperText>
                        </FormControl>

                        {(isUploadingPositions || isUploadingBulkEditPositions) && (
                            <Box sx={{ width: '100%' }}>
                                <LinearProgress />
                                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                    Uploading...
                                </Typography>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={handleUpload}
                                disabled={!file || isUploadingPositions || isUploadingBulkEditPositions}
                                startIcon={!isUploadingPositions && (isBulkEdit ? <FileEdit /> : <Upload />)}
                                sx={{
                                    minWidth: '200px',
                                    maxWidth: '300px',
                                }}
                            >
                                {isUploadingPositions || isUploadingBulkEditPositions
                                    ? isBulkEdit
                                        ? 'Updating...'
                                        : 'Uploading...'
                                    : isBulkEdit
                                      ? 'Update Positions'
                                      : 'Upload Positions'}
                            </Button>
                        </Box>

                        {/* Upload Results Display */}
                        {uploadResult && (
                            <Alert
                                severity={
                                    uploadResult.type === 'success'
                                        ? 'success'
                                        : uploadResult.type === 'partial_success'
                                          ? 'warning'
                                          : 'error'
                                }
                                icon={
                                    uploadResult.type === 'success' ? (
                                        <CheckCircle />
                                    ) : uploadResult.type === 'partial_success' ? (
                                        <AlertCircle />
                                    ) : (
                                        <AlertCircle />
                                    )
                                }
                                action={
                                    <IconButton aria-label="close" color="inherit" size="small" onClick={clearResults}>
                                        <X size={16} />
                                    </IconButton>
                                }
                                sx={{ mb: 2 }}
                            >
                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                    {uploadResult.message}
                                </Typography>

                                {/* Status and Statistics */}
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 500,
                                            color:
                                                uploadResult.type === 'success'
                                                    ? 'success.dark'
                                                    : uploadResult.type === 'partial_success'
                                                      ? 'warning.dark'
                                                      : 'error.dark',
                                        }}
                                    >
                                        Status: {uploadResult.status}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color:
                                                uploadResult.type === 'success'
                                                    ? 'success.dark'
                                                    : uploadResult.type === 'partial_success'
                                                      ? 'warning.dark'
                                                      : 'error.dark',
                                        }}
                                    >
                                        Total Rows: {uploadResult.totalRows} | Success: {uploadResult.successCount} |
                                        Errors: {uploadResult.errorCount}
                                    </Typography>
                                </Box>

                                {/* Success Section - Show if there are successful items */}
                                {uploadResult.successCount > 0 && (
                                    <Box sx={{ mt: 2, mb: uploadResult.errorCount > 0 ? 2 : 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500, color: 'success.dark', mb: 1 }}
                                        >
                                            ✅ {isBulkEdit ? 'Successfully Updated' : 'Successfully Processed'} (
                                            {uploadResult.successCount} items):
                                        </Typography>
                                        {uploadResult.positions && uploadResult.positions.length > 0 && (
                                            <List dense sx={{ py: 0 }}>
                                                {uploadResult.positions.map((position, index) => (
                                                    <ListItem key={index} sx={{ py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'success.main',
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={`${position.title || 'Position'} (ID: ${position.id})`}
                                                            secondary={
                                                                position.positionCode &&
                                                                `Code: ${position.positionCode}`
                                                            }
                                                            sx={{
                                                                '& .MuiListItemText-primary': {
                                                                    fontSize: '0.875rem',
                                                                    color: 'success.dark',
                                                                    fontWeight: 500,
                                                                },
                                                                '& .MuiListItemText-secondary': {
                                                                    fontSize: '0.75rem',
                                                                    color: 'success.main',
                                                                },
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                    </Box>
                                )}

                                {/* Error Section - Show if there are errors */}
                                {uploadResult.errorCount > 0 &&
                                    uploadResult.errors &&
                                    uploadResult.errors.length > 0 && (
                                        <Box sx={{ mt: uploadResult.successCount > 0 ? 2 : 0 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, color: 'error.dark', mb: 1 }}
                                            >
                                                ❌ Errors Found ({uploadResult.errorCount} items):
                                            </Typography>
                                            <List dense sx={{ py: 0 }}>
                                                {uploadResult.errors.map((error, index) => (
                                                    <ListItem key={index} sx={{ py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 24 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'error.main',
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={error}
                                                            sx={{
                                                                '& .MuiListItemText-primary': {
                                                                    fontSize: '0.875rem',
                                                                    color: 'error.dark',
                                                                },
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                            </Alert>
                        )}

                        {uploadResult && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate('/client/positions')}
                                    sx={{
                                        color: '#215263',
                                        borderColor: '#215263',
                                        '&:hover': {
                                            color: '#ED7342',
                                            borderColor: '#ED7342',
                                            backgroundColor: '#FFF4F0',
                                        },
                                    }}
                                >
                                    View Positions
                                </Button>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </StyledCard>
        </StyledContainer>
    );
}
